import React, { useState, useEffect } from "react";
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import AuthForm from "./components";
import Stockview from "./components/Stocklist";
import Reportpage from "./components/Pages/ReportList";
import { ChangePasswordForm } from "./components/Pages/Changepassword";
import ReportDetails from "./components/Pages/Reportcomponents/Report";
import CustomerList from "./components/Pages/customerlist";
import CustomerStocks from "./components/Pages/customerstock";
import CustomerEditPage from "./components/Pages/CustomerEditPage";
import IncidentReportByDate from "./components/Pages/IncidentReportByDate";
import CustomerMap from "./components/Pages/CustomerMap";
import UserManagement from "./components/Pages/UserManagement";
import 'bootstrap/dist/css/bootstrap.min.css';

import Overview from "./components/Overview";
import TopNavBar from "./Dashboard/TopNavBar";
import './App.css';

const App = () => {
    const TIMEOUT_DURATION = 10 * 60 * 1000; // 10 minutes
    const AUTO_LOGOUT_DURATION = 60 * 1000; // 1 minute
    const [showTimeoutModal, setShowTimeoutModal] = useState(false);
    const [userEmail, setUserEmail] = useState(() => {
        return sessionStorage.getItem("userEmail") || null;
    });
    const [userRole, setUserRole] = useState(() => {
        return sessionStorage.getItem("userRole") || null;
    });
    
    // Persist login state in sessionStorage
    const [isLogin, setIsLogin] = useState(() => {
        const stored = sessionStorage.getItem("isLogin");
        return stored ? JSON.parse(stored) : true;
    });
    const navigate = useNavigate();

    // Clear session if it's a fresh page load (not navigation)
    useEffect(() => {
        const isPageRefresh = !window.performance.getEntriesByType('navigation')[0] || 
                              window.performance.getEntriesByType('navigation')[0].type === 'reload';
        
        if (isPageRefresh && sessionStorage.getItem('isLogin') === 'false') {
            // Optional: Keep session on refresh, or clear it
            // Uncomment below to force logout on page refresh:
            // sessionStorage.clear();
            // setIsLogin(true);
        }
    }, []);

    useEffect(() => {
        if (isLogin) return; // Don't track if not logged in

        let timeout;
        const resetTimer = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                setShowTimeoutModal(true);
            }, TIMEOUT_DURATION);
        };

        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(event => window.addEventListener(event, resetTimer));
        resetTimer();

        return () => {
            clearTimeout(timeout);
            events.forEach(event => window.removeEventListener(event, resetTimer));
        };
    }, [isLogin, showTimeoutModal]);

    useEffect(() => {
        if (!showTimeoutModal) return;

        const autoLogoutTimer = setTimeout(() => {
            handleLogout();
        }, AUTO_LOGOUT_DURATION);

        return () => clearTimeout(autoLogoutTimer);
    }, [showTimeoutModal]);

    const handleLoginSuccess = (email, roles) => {
        const role = roles.includes('Admin') ? 'Admin' : 'User';
        setUserEmail(email);
        setUserRole(role);
        sessionStorage.setItem("userEmail", email);
        sessionStorage.setItem("userRole", role);
        setIsLogin(false);
        sessionStorage.setItem("isLogin", JSON.stringify(false));
        navigate("/overview");
    };

    const handleContinueSession = () => {
        setShowTimeoutModal(false);
    };

    const handleLogout = () => {
        setShowTimeoutModal(false);
        setUserEmail(null);
        setUserRole(null);
        sessionStorage.removeItem("userEmail");
        sessionStorage.removeItem("userRole");
        setIsLogin(true);
        sessionStorage.setItem("isLogin", JSON.stringify(true));
        navigate("/");
    };

    if (isLogin) {
        return <AuthForm onLoginSuccess={handleLoginSuccess} />;
    }

    return (
        <div className="app-container">
            <TopNavBar className="top-nav" onLogout={handleLogout} userEmail={userEmail} userRole={userRole} />
            <div className="main-content">
                <Routes>
                    <Route path="/" element={<Navigate to="/overview" />} />
                    <Route path="/overview" element={<Overview userEmail={userEmail} />} />
                    <Route path="/stocklist" element={<Stockview userEmail={userEmail} />} />
                    <Route path="/report" element={<Reportpage userEmail={userEmail} userRole={userRole} />} />
                    <Route path="/incident-report-by-date" element={<IncidentReportByDate userEmail={userEmail} userRole={userRole} />} />
                    <Route path="/users" element={<UserManagement userRole={userRole} />} />
                    <Route path="/details/:reportID" element={<ReportDetails userEmail={userEmail} />} />
                    <Route path="/report/:reportID" element={<ReportDetails userEmail={userEmail} />} />
                    <Route path="/dropoff/:reportID" element={<ReportDetails userEmail={userEmail} />} />
                    <Route path="/changepassword" element={<ChangePasswordForm userEmail={userEmail} />} />
                    <Route path="/customers" element={<CustomerList userName={userEmail} userRole={userRole} />} />
                    <Route path="/customer-map" element={<CustomerMap userEmail={userEmail} userRole={userRole} />} />
                    <Route path="/customer-view/:customerId" element={<CustomerStocks userEmail={userEmail} />} />
                    <Route path="/customer-edit/:customerId" element={<CustomerEditPage />} />
                    {/* <Route path="*" element={<Navigate to="/overview" />} /> */}
                </Routes>
            </div>

            {showTimeoutModal && (
                <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Session Timeout</h5>
                            </div>
                            <div className="modal-body">
                                <p>Your session is about to expire due to inactivity. Would you like to continue?</p>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={handleLogout}>Logout</button>
                                <button className="btn btn-primary" onClick={handleContinueSession}>Continue</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default App;
